import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PagamentiPendentiProvider } from "./contexts/PagamentiPendentiContext";
import { NotificheProvider } from "./contexts/NotificheContext";
import { TenantProvider } from "./contexts/TenantContext";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ErrorBoundary from "./components/ErrorBoundary";
import LoginPage from "./pages/LoginPage";
import PortaleAccettazionePage from "./pages/PortaleAccettazionePage";
import DashboardPage from "./pages/DashboardPage";
import ClientiPage from "./pages/ClientiPage";
import ClienteDetailPage from "./pages/ClienteDetailPage";
import RapportinoPage from "./pages/RapportinoPage";
import ContrattoPage from "./pages/ContrattoPage";
import ContrattiPage from "./pages/ContrattiPage";
import ClienteWizardPage from "./pages/ClienteWizardPage";
import ImpiantiPage from "./pages/ImpiantiPage";
import ImpiantoDetailPage from "./pages/ImpiantoDetailPage";
import TicketsPage from "./pages/TicketsPage";
import TicketDetailPage from "./pages/TicketDetailPage";
import UtentiPage from "./pages/UtentiPage";
import DocumentiPage from "./pages/DocumentiPage";
import MagazzinoPage from "./pages/MagazzinoPage";
import ImpostazioniPage from "./pages/ImpostazioniPage";
import VerificheScadenzaPage from "./pages/VerificheScadenzaPage";
import BackupPage from "./pages/BackupPage";
import ListinoPrezziPage from "./pages/ListinoPrezziPage";
import StatistichePage from "./pages/StatistichePage";
import CalendarioPage from "./pages/CalendarioPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import ReportsPage from "./pages/ReportsPage";
import AvvisiClientiPage from "./pages/AvvisiClientiPage";
import LogEmailPage from "./pages/LogEmailPage";
import ContrattiGestionaliPage from "./pages/ContrattiGestionaliPage";
import ScadenzeHubPage from "./pages/ScadenzeHubPage";
import AnalyticsHubPage from "./pages/AnalyticsHubPage";
import TecnicoMobilePage from "./pages/TecnicoMobilePage";
import FatturazionePage from "./pages/FatturazionePage";
import Layout from "./components/Layout";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public portal accettazione preventivo via QR code - NO auth */}
      <Route path="/p/preventivo/:id" element={<PortaleAccettazionePage />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="clienti" element={<ClientiPage />} />
        <Route path="clienti/nuovo" element={<ClienteWizardPage />} />
        <Route path="clienti/:id" element={<ClienteDetailPage />} />
        <Route path="rapportini/nuovo" element={<RapportinoPage />} />
        <Route path="rapportini/:id" element={<RapportinoPage />} />
        <Route path="contratti" element={<ContrattiPage />} />
        <Route path="contratti/nuovo" element={<ContrattoPage />} />
        <Route path="contratti/:id" element={<ContrattoPage />} />
        <Route path="impianti" element={<ImpiantiPage />} />
        <Route path="impianti/:id" element={<ImpiantoDetailPage />} />
        <Route path="abbonamenti" element={<Navigate to="/contratti" replace />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/:id" element={<TicketDetailPage />} />
        <Route path="utenti" element={<UtentiPage />} />
        <Route path="documenti" element={<DocumentiPage />} />
        <Route path="fatturazione" element={<FatturazionePage />} />
        <Route path="magazzino" element={<MagazzinoPage />} />
        <Route path="impostazioni" element={<ImpostazioniPage />} />
        <Route path="verifiche-scadenza" element={<VerificheScadenzaPage />} />
        <Route path="rinnovi-abbonamenti" element={<Navigate to="/contratti" replace />} />
        <Route path="backup" element={<BackupPage />} />
        <Route path="listino-prezzi" element={<ListinoPrezziPage />} />
        <Route path="statistiche" element={<StatistichePage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="avvisi-clienti" element={<AvvisiClientiPage />} />
        <Route path="log-email" element={<LogEmailPage />} />
        <Route path="contratti-gestionali" element={<ContrattiGestionaliPage />} />
        <Route path="scadenze" element={<ScadenzeHubPage />} />
        <Route path="analytics" element={<AnalyticsHubPage />} />
        <Route path="tecnico" element={<TecnicoMobilePage />} />
        <Route path="calendario" element={<CalendarioPage />} />
        <Route path="super-admin" element={<SuperAdminPage />} />
      </Route>
    </Routes>
  );
};

// Wrapper che monta PagamentiPendentiProvider, NotificheProvider e TenantProvider solo quando l'utente è loggato
const AuthenticatedProviders = ({ children }) => {
  const { user } = useAuth();
  
  // Se non loggato, mostra direttamente i children senza i provider
  if (!user) {
    return children;
  }
  
  return (
    <TenantProvider>
      <PagamentiPendentiProvider>
        <NotificheProvider>
          {children}
        </NotificheProvider>
      </PagamentiPendentiProvider>
    </TenantProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AuthenticatedProviders>
              <AppRoutes />
              <Toaster position="top-right" />
              <PWAInstallPrompt />
            </AuthenticatedProviders>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
